// HomePage — Ros landing. Time-aware greeting, the Ask Ros prompt (starts a new chat), a dismissible
// Ros suggestion card, and Recent chats + Recent artifacts side by side (real /api data).
// Studio Cyan/Stone tokens. (Artifact retention status is deferred per the ticket.)

import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { useNavigate } from 'react-router-dom'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import BuildIcon from '@mui/icons-material/Build'
import PublicIcon from '@mui/icons-material/Public'
import { useRosShell } from '../RosShellContext'
import { config } from '../config/environment'
import { SERIF_HEADING } from '../theme'
import {useRosFeatures} from '../access/useRosFeatures'
import {listRosCatalog, type RosCatalogEntry} from './registryCatalogApi'
import {resolveIcon} from '../config/registryIcons'

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

interface ArtifactRow { id: string; type: string; title: string; sessionId: string; publishedAt: string }

const TYPE_LABEL: Record<string, string> = {
  crosstabs: 'Crosstabs', website: 'Website', survey: 'Survey', 'app-artifact': 'App Artifact',
}

export function HomePage() {
  const shell = useRosShell()
  const {snapshot, internalGeneralPurpose, enabled, authFetch} = useRosFeatures()
  const navigate = useNavigate()
  const artifactsMgmtEnabled = enabled('artifacts-mgmt')
  const showTools = enabled('ros-tools')
  const [text, setText] = useState('')
  const [suggestionDismissed, setSuggestionDismissed] = useState(false)
  const [artifacts, setArtifacts] = useState<ArtifactRow[]>([])
  const [tools, setTools] = useState<RosCatalogEntry[]>([])
  const [toolsError, setToolsError] = useState(false)
  const [toolsAttempt, setToolsAttempt] = useState(0)

  // Prefer the Settings preferred/full name over the JWT name. Hold the name until settings resolve so
  // it doesn't flicker (email/JWT-name flashing, then snapping to the Settings name) on first load.
  const greetName = shell.userSettingsLoaded
    ? (shell.userSettings?.preferredName?.trim() || shell.userSettings?.fullName?.trim() || shell.name || '')
    : ''
  const firstName = greetName.split(' ')[0]
  // Suffix: name once known; a gentle "…" while settings load (so the name slides in, not pops from a
  // bare period); a plain "." only if loaded with genuinely no name.
  const greetingSuffix = firstName ? `, ${firstName}.` : (shell.userSettingsLoaded ? '.' : '…')

  const submit = () => {
    const msg = text.trim()
    if (!msg) return
    shell.startNewChatWithMessage(msg)
    setText('')
  }

  // Recent artifacts: flatten the grouped catalog, newest version per artifact, top 5 by publishedAt.
  useEffect(() => {
    let cancelled = false
    authFetch(`${config.API_CF_URL}/api/r3/artifacts`)
      .then(r => r.ok ? r.json() : { groups: [] })
      .then((data: { groups?: Array<{ type: string; items: Array<{ versions?: ArtifactRow[] } & ArtifactRow> }> }) => {
        if (cancelled) return
        const rows: ArtifactRow[] = (data.groups ?? [])
          .filter(group => artifactsMgmtEnabled || group.type !== 'markdown')
          .flatMap(group => group.items)
          .map(item => {
            const newest = item.versions?.[0] ?? item
            return {id: item.id, type: item.type, title: newest.title ?? item.title, sessionId: item.sessionId, publishedAt: newest.publishedAt ?? ''}
          })
        rows.sort((a, b) => (b.publishedAt > a.publishedAt ? 1 : -1))
        setArtifacts(rows.slice(0, 5))
      })
      .catch(() => { /* best-effort */ })
    return () => { cancelled = true }
  }, [artifactsMgmtEnabled, authFetch])

  useEffect(() => {
    if (!showTools) { setTools([]); setToolsError(false); return }
    let cancelled = false
    setToolsError(false)
    listRosCatalog(shell.apiBaseUrl, authFetch)
      .then(entries => { if (!cancelled) setTools(entries.filter(entry => entry.name.toLowerCase() !== 'skills').slice(0, 8)) })
      .catch(() => { if (!cancelled) { setTools([]); setToolsError(true) } })
    return () => { cancelled = true }
  }, [showTools, shell.apiBaseUrl, authFetch, toolsAttempt])

  const recentChats = useMemo(() => shell.sessions.slice(0, 5), [shell.sessions])
  const profileCopy = internalGeneralPurpose
    ? 'Ros is your general-purpose work assistant for writing, coding, analysis, planning, and research.'
    : snapshot.userType === 'external'
      ? 'Ros supports your market research work and shows the specialist actions available to you.'
      : "Tell me what you're working on and I'll pick the right research workflow."
  const placeholder = internalGeneralPurpose
    ? 'Ask Ros about writing, coding, analysis, planning, or research…'
    : 'Ask Ros about your market research…'
  const openTool = (tool: RosCatalogEntry) => {
    const href = /^https?:\/\//i.test(tool.url) ? tool.url : `${config.STUDIO_URL}${tool.url}`
    window.open(href, '_blank', 'noopener')
  }

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto', px: 4, py: 6 }}>
      <Typography variant="h2" sx={{ color: 'text.primary' }}>
        {greeting(new Date().getHours())}{greetingSuffix}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
        Pick up where you left off or start something new.
      </Typography>

      {/* Ros suggestion card (dismissible) + Ask Ros prompt */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 5 }}>
        {!suggestionDismissed && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ ...SERIF_HEADING, fontSize: '0.95rem', color: 'text.primary', mb: 0.25 }}>Ros</Typography>
              <Typography sx={{ fontSize: '0.88rem', color: 'text.primary' }}>
                {profileCopy}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setSuggestionDismissed(true)} sx={{ color: 'text.disabled' }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <TextField
            fullWidth multiline maxRows={6} value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
            placeholder={placeholder}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
          />
          <Button variant="contained" disabled={!text.trim()} onClick={submit} endIcon={<SendIcon />} sx={{ py: 1.4, px: 2.5, whiteSpace: 'nowrap' }}>Send</Button>
        </Box>
      </Paper>

      {/* Recent chats + Recent artifacts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 5 }}>
        <Section title="Recent chats" onViewAll={() => navigate('/chats')}>
          {recentChats.length === 0 ? <Empty text="No chats yet." /> : recentChats.map((s, i) => (
            <RowItem
              key={s.id} first={i === 0}
              primary={s.intentSummary || 'Untitled chat'}
              meta={new Date(s.lastActiveAt ?? s.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              onClick={() => shell.selectSession(s.id)}
            />
          ))}
        </Section>

        <Section title="Recent artifacts" onViewAll={() => navigate('/artifacts')}>
          {artifacts.length === 0 ? <Empty text="No artifacts yet." /> : artifacts.map((a, i) => (
            <RowItem
              key={a.id} first={i === 0}
              primary={a.title}
              tag={TYPE_LABEL[a.type] ?? a.type}
              meta={a.publishedAt ? new Date(a.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
              onClick={() => navigate('/artifacts')}
            />
          ))}
        </Section>
      </Box>

      {/* Tools grid */}
      {showTools && <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h3" sx={SERIF_HEADING}>Tools</Typography>
        {toolsError && <Button size="small" onClick={() => setToolsAttempt(value => value + 1)} sx={{ textTransform: 'none' }}>Retry</Button>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        {tools.map(tool => {
          const fallback = tool.kind === 'public_app' ? <PublicIcon /> : tool.kind === 'tool' ? <BuildIcon /> : <AppsOutlinedIcon />
          return (
          <Paper
            key={tool.id} variant="outlined"
            onClick={() => openTool(tool)}
            sx={{ p: 2, borderRadius: 2.5, cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: 'primary.main', boxShadow: 1 } }}
          >
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', mb: 1.5 }}>{resolveIcon(tool.icon, fallback)}</Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mb: 0.5 }}>{tool.name}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.45 }}>{tool.description}</Typography>
          </Paper>
        )})}
      </Box>
      </>}
    </Box>
  )
}

function Section({ title, onViewAll, children }: { title: string; onViewAll: () => void; children: React.ReactNode }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ ...SERIF_HEADING, fontSize: '1.15rem' }}>{title}</Typography>
        <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>View all</Button>
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>{children}</Paper>
    </Box>
  )
}

function RowItem({ primary, meta, tag, first, onClick }: { primary: string; meta: string; tag?: string; first: boolean; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.4, cursor: 'pointer', borderTop: first ? 'none' : '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{primary}</Typography>
        {tag && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{tag}</Typography>}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{meta}</Typography>
    </Box>
  )
}

function Empty({ text }: { text: string }) {
  return <Box sx={{ px: 2, py: 3, textAlign: 'center' }}><Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{text}</Typography></Box>
}
